import type { Database } from 'sql.js';
import type { MasterDataApplyCounts } from '../../domain/ports/CatalogDownloadPort.ts';
import { parseMasterDataJson } from './parseMasterDataJson.ts';
import { applyMasterDataBundleToDb } from './sqlJsApplyMasterDataBundle.ts';

/** Persists the last successfully applied server catalog revision (e.g. localStorage). */
export type CatalogRevisionPersistence = {
  getLastAppliedRevision(): string | null;
  persistAppliedRevision(revision: string): void;
};

export type ApplyMasterDataOptions = {
  /** When true, apply even if `catalog_revision` matches the last persisted revision. */
  forceApply?: boolean;
  /** Row-level progress while merging bundle tables into SQLite. */
  onApplyProgress?: (loaded: number, total: number) => void;
};

/**
 * Parses remote master data, skips duplicate `catalog_revision` for idempotent downloads,
 * and merges rows into sql.js.
 */
export async function applyMasterDataJsonResponseToDb(
  db: Database,
  raw: unknown,
  revisionStore: CatalogRevisionPersistence,
  options?: ApplyMasterDataOptions,
): Promise<MasterDataApplyCounts> {
  const parsed = parseMasterDataJson(raw);
  if (!parsed) {
    throw new Error('Catálogo maestro inválido: JSON no reconocido o formato incorrecto.');
  }
  const { bundle, catalogRevision } = parsed;
  const last = revisionStore.getLastAppliedRevision();
  if (
    !options?.forceApply &&
    catalogRevision != null &&
    last != null &&
    catalogRevision === last
  ) {
    return {
      comunasEscan: 0,
      participantes: 0,
      proveedores: 0,
      usuarios: 0,
      puntosEntregaCatalogo: 0,
      skippedDuplicateRevision: true,
    };
  }
  const counts = await applyMasterDataBundleToDb(db, bundle, options?.onApplyProgress);
  if (catalogRevision != null) revisionStore.persistAppliedRevision(catalogRevision);
  return counts;
}
